import { useField, useFormikContext } from 'formik';
import { memo, useCallback } from 'react';
import { GMFormPasswordFieldProps, GMPasswordField } from '..';
import { ErrorLevel } from '../../../../API/Enums';
import { GoMotoComponent } from '../../../../API/GoMoto.types';

/**
 * PasswordField for use in a Formik.
 *
 * Sets the value, onChange, error, & helperText internally
 */
const FormPasswordField: GoMotoComponent<GMFormPasswordFieldProps> = memo(({
    validateBeforeSubmit,
    warningText,
    helperText,
    onChange: propsOnChange,
    onBlur: propsOnBlur,
    ...props
}) => {
    const [field, meta] = useField(props.name);
    const { setFieldValue, submitCount, setFieldTouched } = useFormikContext();

    const onChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        if (propsOnChange) {
            propsOnChange(e);
        }
        else {
            setFieldValue(props.name, e.target.value);
        }
    }, [propsOnChange, props.name, setFieldValue]);

    const onBlur = useCallback((e: React.FocusEvent<HTMLInputElement>) => {
        setFieldTouched(props.name, true);
        if (propsOnBlur) {
            propsOnBlur(e);
        }
    }, [propsOnBlur, setFieldTouched, props.name]);

    const getErrorLevel = (): ErrorLevel => {
        if (!!meta.error && meta.touched && (validateBeforeSubmit || submitCount > 0)) {
            return ErrorLevel.Error;
        }

        if (!!warningText) {
            return ErrorLevel.Warning;
        }

        return ErrorLevel.None;
    };

    const getHelperText = (): GMFormPasswordFieldProps['helperText'] => {
        if (meta.error && meta.touched && (validateBeforeSubmit || submitCount > 0)) {
            return meta.error;
        }

        if (warningText) {
            return warningText;
        }

        return helperText;
    };

    return (
        <GMPasswordField
            value={field.value}
            onChange={onChange}
            error={getErrorLevel()}
            helperText={getHelperText()}
            onBlur={onBlur}
            {...props}
        />
    );
});

export default FormPasswordField;